import { useRef, useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Id } from '../../../../convex/_generated/dataModel';
import { Download, Wand2, RotateCcw } from 'lucide-react';
import posthog from 'posthog-js';

interface ResultViewProps {
  originalVideoUrl: string;
  processedVideoUrl: string;
  processedStorageId: string;
  projectId: string;
  videoStorageId: string;
  onReset: () => void;
}

export function ResultView({
  originalVideoUrl,
  processedVideoUrl,
  processedStorageId,
  projectId,
  videoStorageId,
  onReset,
}: ResultViewProps) {
  const navigate = useNavigate();
  const originalRef = useRef<HTMLVideoElement>(null);
  const processedRef = useRef<HTMLVideoElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const saveEditorOverlays = useMutation(api.projects.saveEditorOverlays);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Sync playback between videos (desktop)
  const handlePlay = useCallback(() => {
    if (isMobile) return;
    originalRef.current?.play();
    processedRef.current?.play();
  }, [isMobile]);

  const handlePause = useCallback(() => {
    if (isMobile) return;
    originalRef.current?.pause();
    processedRef.current?.pause();
  }, [isMobile]);

  const handleSeeked = useCallback(() => {
    if (isMobile) return;
    if (originalRef.current && processedRef.current) {
      processedRef.current.currentTime = originalRef.current.currentTime;
    }
  }, [isMobile]);

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    posthog.capture('tool_download_clicked');
    try {
      const response = await fetch(processedVideoUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'background-removed.webm';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setIsDownloading(false);
    }
  }, [processedVideoUrl]);

  const handleEditInReelCat = useCallback(async () => {
    setIsNavigating(true);
    posthog.capture('tool_edit_clicked');
    try {
      // Build a minimal overlay JSON for the editor
      const overlays = [
        {
          id: 1,
          type: 'video',
          src: processedVideoUrl,
          originalSrc: originalVideoUrl,
          removeBgEnabled: true,
          removeBgStorageId: processedStorageId,
          mediaStorageId: videoStorageId,
          startFrame: 0,
          endFrame: 900, // Will be adjusted by editor
          x: 0,
          y: 0,
          width: 100,
          height: 100,
          rotation: 0,
          opacity: 1,
          volume: 1,
        },
      ];

      await saveEditorOverlays({
        projectId: projectId as Id<'projects'>,
        overlaysJson: JSON.stringify(overlays),
      });

      navigate(`/editor?projectId=${projectId}`);
    } catch (err) {
      console.error('Failed to navigate to editor:', err);
      setIsNavigating(false);
    }
  }, [processedVideoUrl, originalVideoUrl, processedStorageId, videoStorageId, projectId, saveEditorOverlays, navigate]);

  const checkerboardStyle = {
    backgroundImage: 'repeating-conic-gradient(#80808030 0% 25%, transparent 0% 50%)',
    backgroundSize: '16px 16px',
  };

  return (
    <div className="space-y-8">
      {/* Video comparison */}
      {isMobile ? (
        // Mobile: single video with toggle
        <div className="space-y-4">
          <div className="flex items-center justify-center gap-2 bg-muted rounded-lg p-1">
            <button
              onClick={() => setShowOriginal(false)}
              className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                !showOriginal ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'
              }`}
            >
              Background Removed
            </button>
            <button
              onClick={() => setShowOriginal(true)}
              className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                showOriginal ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'
              }`}
            >
              Original
            </button>
          </div>
          <div
            className="relative aspect-video rounded-xl overflow-hidden"
            style={showOriginal ? {} : checkerboardStyle}
          >
            <video
              src={showOriginal ? originalVideoUrl : processedVideoUrl}
              className="w-full h-full object-contain"
              controls
              loop
              autoPlay
              muted
              playsInline
            />
          </div>
        </div>
      ) : (
        // Desktop: side-by-side
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground text-center">Original</p>
            <div className="aspect-video rounded-xl overflow-hidden bg-muted">
              <video
                ref={originalRef}
                src={originalVideoUrl}
                className="w-full h-full object-contain"
                controls
                loop
                muted
                playsInline
                onPlay={handlePlay}
                onPause={handlePause}
                onSeeked={handleSeeked}
              />
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground text-center">Background Removed</p>
            <div
              className="aspect-video rounded-xl overflow-hidden"
              style={checkerboardStyle}
            >
              <video
                ref={processedRef}
                src={processedVideoUrl}
                className="w-full h-full object-contain"
                loop
                muted
                playsInline
              />
            </div>
          </div>
        </div>
      )}

      {/* CTAs */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <button
          onClick={handleDownload}
          disabled={isDownloading}
          className="flex items-center gap-2 px-6 py-3 border border-border rounded-lg font-medium hover:bg-muted transition-colors disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {isDownloading ? 'Downloading...' : 'Download'}
        </button>
        <button
          onClick={handleEditInReelCat}
          disabled={isNavigating}
          className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50"
        >
          <Wand2 className="w-4 h-4" />
          {isNavigating ? 'Opening editor...' : 'Edit in ReelCat'}
        </button>
      </div>

      <p className="text-center text-sm text-muted-foreground">
        Take it further &mdash; add captions, effects, and music in ReelCat
      </p>

      {/* Reset */}
      <div className="text-center">
        <button
          onClick={onReset}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Remove another video
        </button>
      </div>
    </div>
  );
}
